import { datadogLogs } from '@datadog/browser-logs';
import { FC, useCallback } from 'react';
import { cleanFileName } from 'src/helpers';
import { IProof } from 'src/ts/interfaces';
import { IListCheckCandidate } from 'src/ts/interfaces/listCheck';
import { fieldText } from '../../helpers/listCheckHelpers';
import { ListCheckRegistry, ListCheckRegistryDocument, ListCheckRegistryFacts } from '../../styles';
import { DocumentPane, RowReference, useReviewedUrl } from './CandidateTile';
import { listCheckCopy } from './listCheckCopy';

interface RegistryCardProps {
  checkKey: string;
  row: IListCheckCandidate;
  providers: string[];
  currentProof?: IProof;
}

const RegistryCard: FC<RegistryCardProps> = ({ checkKey, row, providers, currentProof }) => {
  const proofId = currentProof?.id;
  const provider = row.fields?.payroll_provider;
  const reviewedUrl = useReviewedUrl(currentProof);
  const logError = useCallback(
    (error: unknown) => {
      datadogLogs.logger.error('List check registry document render failed', {
        error,
        proof_id: proofId,
        check_key: checkKey
      });
    },
    [proofId, checkKey]
  );

  return (
    <ListCheckRegistry data-testid="list-check-registry-card">
      <ListCheckRegistryFacts>
        <dt>{listCheckCopy.company}</dt>
        <dd>{row.label}</dd>
        {row.row_ref && (
          <>
            <dt>{listCheckCopy.registryRow}</dt>
            <dd>
              <RowReference rowRef={row.row_ref} rowUrl={row.row_url} />
            </dd>
          </>
        )}
        <dt>{listCheckCopy.expectedProvider}</dt>
        <dd>{provider ? fieldText(provider) : '—'}</dd>
        {providers.length > 1 && (
          <>
            <dt>{listCheckCopy.registeredProviders}</dt>
            <dd>{providers.join(', ')}</dd>
          </>
        )}
      </ListCheckRegistryFacts>
      <ListCheckRegistryDocument>
        <DocumentPane
          containerId="list-check-registry-reviewed"
          title={listCheckCopy.documentUnderReview}
          url={reviewedUrl}
          fileName={cleanFileName(currentProof?.fileName ?? 'document')}
          fileId={proofId ?? 'none'}
          unavailableText="This document is not available to view."
          onError={logError}
        />
      </ListCheckRegistryDocument>
    </ListCheckRegistry>
  );
};

export default RegistryCard;
